import type { Component } from "solid-js";
import {
	type ComponentProps,
	createEffect,
	createSignal,
	type JSX,
	on,
	splitProps
} from "solid-js";
import { twMerge } from "tailwind-merge";
import classes from "./Image.module.sass";

type ImageOwnProps = {
	icon?: JSX.Element;
	containerClassName?: string;
	isNeedToCancelAnimateOnPending?: boolean;
	containerRef?: HTMLDivElement | ((el: HTMLDivElement) => void);
	isForcedPending?: boolean;
	pendingClassName?: string;
	containerStyles?: JSX.HTMLAttributes<HTMLDivElement>["style"];
};

type ImageProps = ImageOwnProps & Omit<ComponentProps<"img">, keyof ImageOwnProps>;

export const CustomImage: Component<ImageProps> = (props) => {
	const [isLoaded, setIsLoaded] = createSignal(false);
	let _img: HTMLImageElement;

	createEffect(
		on(
			() => props.src,
			(cur, prev) => {
				if (cur === prev) {
					return;
				}
				setIsLoaded(false);
			},
			{ defer: true }
		)
	);
	const [notNativeAttributes, others] = splitProps(props, [
		"pendingClassName",
		"icon",
		"children",
		"class",
		"classList",
		"containerClassName",
		"isNeedToCancelAnimateOnPending",
		"containerRef",
		"isForcedPending",
		"ref",
		"onLoad",
		"onload"
	]);
	const assignRef = (ref: HTMLImageElement) => {
		_img = ref;
		if (!props.ref) {
			return;
		}
		if (typeof props.ref === "function") {
			props.ref(ref);
			return;
		}
		props.ref = ref;
	};
	return (
		<div
			ref={notNativeAttributes.containerRef}
			class={twMerge("relative h-full w-full", notNativeAttributes.containerClassName)}
			classList={{
				[`${classes.loading} ${props.pendingClassName || ""}`]:
					notNativeAttributes.isForcedPending ||
					(!isLoaded() && !notNativeAttributes.isNeedToCancelAnimateOnPending)
			}}
			style={props.containerStyles}
		>
			<img
				ref={assignRef}
				src={props.src}
				alt={props.alt || "Image"}
				onLoad={(e) => {
					setIsLoaded(true);
					if (typeof props.onLoad === "function") {
						props.onLoad?.(e);
					}
					if (typeof props.onload === "function") {
						props.onload(e);
					}
				}}
				class={twMerge(
					"relative box-border h-full w-full rounded-md bg-center object-cover",
					notNativeAttributes.class
				)}
				{...others}
			/>
			{props.children}
		</div>
	);
};
